/*
  Shared Splide carousel driver.
  ------------------------------
  Mounts any `.splide[data-carousel]` element, but only once its slides overflow
  the track — below that the markup stays a plain static row, which is why every
  caller ships working no-JS CSS for the un-mounted state.

  Everything that differs between callers is a data attribute; everything else
  (overflow gating, slide promotion, progress bar, editor lifecycle) lives here.

  Attributes, all optional unless noted:
    data-carousel               marker; required for this script to touch the element
    data-layout                 'fixed' | 'peek' | 'per-page' | 'auto'  (default 'per-page')
    data-layout-mobile          same values; overrides data-layout below 750px
    data-focus                  'center' to keep the active slide centred
    data-trim-space             'true' | 'false'                (default 'true')
    data-card-width             px, layout=fixed
    data-card-width-mobile      px, layout=fixed below 750px (falls back to data-card-width)
    data-per-view-mobile        float, layout=peek   (e.g. 1.2 → 20% of the next card shows)
    data-per-view               float, layout=peek, >=750px
    data-per-page-mobile        int, layout=per-page
    data-per-page               int, layout=per-page, >=750px
    auto layout                 Splide autoWidth — natural slide widths (logo rows)
    data-gap                    px, >=750px
    data-gap-mobile             px
    data-drag                   'free' | 'step'                 (default 'free')
                                fade type always uses step — free-drag cannot
                                scroll a fade track and snaps back to slide 0
    data-omit-end               'true' | 'false'                (default 'true')
    data-type                   'slide' | 'fade' | 'loop'       (default 'slide')
                                fade cross-dissolves in place; Splide requires
                                one slide per page for it
                                loop clones slides for infinite paging
    data-speed                  ms for a move/dissolve             (default 400)
    data-easing                 CSS easing for move/dissolve       (Splide default)
    data-force-mount            'true' to mount even when slides fit the track
                                (needed for loop/center carousels with few cards)
    data-mount-max-width        px; never mount above this viewport width, for
                                rows that are only a carousel on small screens
    data-arrows                 'true' | 'false'; pre-rendered `.splide__arrows`
                                markup inside the root is adopted as-is
    data-prev-label             translated arrow aria-labels; Splide overwrites
    data-next-label             these on every move, so they cannot live in markup
    data-pagination             'dots' | 'progress' | 'none'
    data-overflow-tolerance     px slack in the overflow test      (default 1)
    data-dot-label              translated remote-dot aria-label; %INDEX% is replaced
                                with the slide number
    data-padding-inline         'section' → Splide track padding from
                                --section-padding-inline (both sides).
                                'custom' → both sides from --carousel-padding-inline
                                on the carousel. Use with a full-bleed track
                                (negative margin) so cards can overflow the page
                                margin mid-scroll while the first/last card still
                                inset at the ends. Splide writes inline padding,
                                so CSS alone cannot do this.

  Remote dots: a section that wants the dots somewhere the carousel can't reach —
  up in a header row, say — renders an empty `[data-carousel-dots-slot]` element
  anywhere inside itself, optionally with `data-slides-per-dot`. This script fills
  it; sections with no such element get no remote dots. The slot is left empty when
  there is nothing to page through, so CSS can hide it with `:empty` instead of
  tracking overflow state.

  They follow `data-pagination`: nothing is rendered under 'none', and under 'dots'
  they replace Splide's built-in pagination rather than showing alongside it.
  'progress' pairs the bar with them (mini-doorways puts dots in its header).

  Slides: Shopify wraps every theme block in `.shopify-block`, so those wrappers —
  not the block markup inside them — are promoted to Splide slides.
*/
(function () {
  if (window.themeCarousel) return;

  function num(el, name, fallback) {
    const raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function bool(el, name, fallback) {
    const raw = el.getAttribute(name);
    if (raw === null) return fallback;
    return raw === 'true';
  }

  // Resolved --section-padding-inline for Splide (needs a concrete length, not a
  // custom-property reference). Falls back through the carousel, then :root.
  function sectionInlinePadding(el) {
    let value = getComputedStyle(el).getPropertyValue('--section-padding-inline').trim();
    if (!value) {
      value = getComputedStyle(document.documentElement)
        .getPropertyValue('--section-padding-inline')
        .trim();
    }
    return value || '0px';
  }

  // --carousel-padding-inline may still be a var() reference. Splide needs px.
  function customInlinePadding(el) {
    const value = getComputedStyle(el).getPropertyValue('--carousel-padding-inline').trim();
    if (!value) return '';
    if (/^-?\d+(\.\d+)?px$/.test(value) || value === '0') return value;

    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;padding-left:' + value;
    el.appendChild(probe);
    const resolved = getComputedStyle(probe).paddingLeft;
    probe.remove();
    return resolved;
  }

  function paddingOption(el) {
    const mode = el.getAttribute('data-padding-inline');
    if (mode !== 'section' && mode !== 'custom') return null;

    const pad = mode === 'section' ? sectionInlinePadding(el) : customInlinePadding(el);
    if (!pad) return null;

    return { left: pad, right: pad };
  }

  // Peek layouts size slides by fraction-of-track so a partial next slide shows.
  // Track `padding` can't do this: it is reserved permanently and leaves a gap at
  // the end. Fractional perPage freezes the drag, hence fixedWidth + calc.
  //
  // Only the gaps *between* visible slides come off the track: 2.2 per view shows
  // slide, gap, slide, gap, sliver — ceil(visible) - 1. Floor would be wrong for a
  // whole number of slides. The un-mounted CSS mirrors this via --carousel-peek-gaps.
  function peekWidth(visible, gap) {
    const gaps = Math.max(0, Math.ceil(visible) - 1);
    return 'calc((100% - ' + gaps * gap + 'px) / ' + visible + ')';
  }

  // Every branch names autoWidth, fixedWidth and perPage: a carousel can use a
  // different layout per breakpoint, and Splide merges the breakpoint over the
  // base, so a key left unset would leak the mobile sizing into desktop. The
  // layouts that size slides themselves pin perPage to Splide's own default.
  function sizingFor(el, layout, gap, mobile) {
    if (layout === 'auto') {
      return { autoWidth: true, fixedWidth: null, perPage: 1 };
    }
    if (layout === 'fixed') {
      const cardWidthAttr = mobile && el.hasAttribute('data-card-width-mobile')
        ? 'data-card-width-mobile'
        : 'data-card-width';
      return {
        autoWidth: false,
        fixedWidth: num(el, cardWidthAttr, 220) + 'px',
        perPage: 1
      };
    }
    if (layout === 'peek') {
      const view = mobile
        ? num(el, 'data-per-view-mobile', 1.2)
        : num(el, 'data-per-view', 2.2);
      return { autoWidth: false, fixedWidth: peekWidth(view, gap), perPage: 1 };
    }
    const perPage = mobile
      ? num(el, 'data-per-page-mobile', 1)
      : num(el, 'data-per-page', 3);
    return { autoWidth: false, fixedWidth: null, perPage: perPage };
  }

  // Splide has no built-in progress bar, so drive it from the track position
  // rather than the slide index — that keeps it truthful during a free drag.
  function driveProgressBar(splide, el) {
    const bar = el.querySelector('.splide__progress__bar');
    if (!bar) return;

    function rateFor(pos) {
      const move = splide.Components.Move;
      const start = move.getLimit(false);
      const end = move.getLimit(true);
      if (end === start) return 1;
      return Math.min(Math.max((pos - start) / (end - start), 0), 1);
    }

    function setBar(rate) {
      bar.style.width = 100 * rate + '%';
    }

    // Arrow/programmatic moves animate to the destination.
    splide.on('move', function (index, prev, dest) {
      setBar(rateFor(splide.Components.Move.toPosition(dest)));
    });
    // Live position while dragging/scrolling.
    splide.on('mounted dragging scrolled moved updated resized', function () {
      setBar(rateFor(splide.Components.Move.getPosition()));
    });
    // Follow the finger 1:1 while dragging (no easing lag).
    splide.on('drag', function () { bar.style.transition = 'none'; });
    splide.on('dragged', function () { bar.style.transition = ''; });
  }

  // The slot is section-level but carousels are merchant-placed, so a section can
  // hold more than one. First to mount owns it; the rest keep their own in-carousel
  // pagination rather than fighting over the same element. Returns null when there
  // is no slot, or when another carousel already owns it.
  function claimDotsSlot(el) {
    // Prefer a slot inside this carousel so tabbed / multi-carousel sections
    // each own their own dots. Fall back to the first section-level slot
    // (e.g. dots in a shared header) when the carousel has none of its own.
    let slot = el.querySelector('[data-carousel-dots-slot]');
    if (!slot) {
      const root = el.closest('.shopify-section');
      slot = root && root.querySelector('[data-carousel-dots-slot]');
    }
    if (!slot) return null;
    if (slot.__carouselOwner && slot.__carouselOwner !== el) return null;
    slot.__carouselOwner = el;
    return slot;
  }

  // Dots rendered outside the carousel (e.g. up in a section header). One dot per
  // `perDot` slides, so a drag that advances a couple of slides doesn't skip a dot.
  function driveRemoteDots(splide, el, slot) {
    const perDot = Math.max(1, num(slot, 'data-slides-per-dot', 1));
    // Translated by Liquid, with the slide number left as a %INDEX% placeholder
    // because this file is a plain asset and cannot call the `t` filter itself.
    const dotLabel = el.getAttribute('data-dot-label') || '%INDEX%';
    let dots = [];

    // Visible arrows already expose paging to assistive tech and the keyboard,
    // which makes a matching row of dots a duplicate control — so there the dots
    // are decorative: hidden from the a11y tree and out of the tab order
    // together, since aria-hidden on a focusable control is itself a violation.
    // Pointer users keep them either way.
    //
    // Test the arrows' rendered visibility rather than `data-arrows`: several
    // sections only show arrows from 750px up, and below that the dots are the
    // sole way to page, so they have to stay exposed. buildDots re-runs on
    // resize, which is what keeps this in step with the breakpoint.
    function arrowsAreVisible() {
      const arrow = el.querySelector('.splide__arrow');
      return Boolean(arrow) && arrow.getClientRects().length > 0;
    }

    function syncDots() {
      if (!dots.length) return;
      const active = Math.min(Math.floor(splide.index / perDot), dots.length - 1);
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === active);
        dots[i].setAttribute('aria-current', i === active ? 'true' : 'false');
      }
    }

    function buildDots() {
      slot.textContent = '';
      dots = [];

      const end = splide.Components.Controller.getEnd();
      const total = Math.ceil((end + 1) / perDot);
      // A single dot conveys nothing — leaving the slot empty also lets callers
      // hide it with :empty rather than tracking overflow state in CSS.
      if (total < 2) return;

      const decorative = arrowsAreVisible();

      const list = document.createElement('ul');
      list.className = 'carousel__dots';
      if (decorative) list.setAttribute('aria-hidden', 'true');

      for (let i = 0; i < total; i++) {
        (function (index) {
          const item = document.createElement('li');
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          if (decorative) dot.tabIndex = -1;
          dot.setAttribute('aria-label', dotLabel.replace('%INDEX%', index * perDot + 1));
          dot.addEventListener('click', function () {
            splide.go(Math.min(index * perDot, splide.Components.Controller.getEnd()));
          });
          item.appendChild(dot);
          list.appendChild(item);
          dots.push(dot);
        })(i);
      }

      slot.appendChild(list);
      syncDots();
    }

    // `mounted` can fire before `.is-overflow` is applied (Splide often skips
    // the initial overflow event; build() then adds the class after mount).
    // Rebuild on a microtask so arrowsAreVisible() sees the arrows that just
    // appeared, and on `overflow` when Splide does report it later.
    splide.on('mounted', function () {
      queueMicrotask(buildDots);
    });
    splide.on('updated resized overflow', buildDots);
    splide.on('move moved scrolled dragged', syncDots);
  }

  function promoteSlides(el) {
    // Theme blocks wrap each item in .shopify-block. Stamp `.carousel__slide` on
    // contentful children so static-row CSS sizes them before (or without) mount.
    // Empty wrappers are skipped — matching `:not(:has(*))` in carousel.css.
    const list = el.querySelector('.splide__list');
    if (!list) return;

    for (let i = 0; i < list.children.length; i++) {
      const child = list.children[i];
      if (child.firstElementChild || child.classList.contains('carousel__slide')) {
        child.classList.add('carousel__slide');
      }
    }
  }

  const missingAttribute = '__carousel_missing__';
  const semanticAttributes = ['role', 'aria-label', 'aria-roledescription', 'tabindex'];

  // Splide's own `focusableNodes` misses media and scripted controls, so widen the
  // net: nothing inside an out-of-view slide should be reachable by keyboard.
  const focusableSelector =
    'a[href], button, input, select, textarea, iframe, audio[controls], video[controls], ' +
    '[contenteditable]:not([contenteditable="false"]), [tabindex], [role="button"]';

  function originalAttributeName(name) {
    return 'data-carousel-original-' + name;
  }

  function rememberAttribute(element, name) {
    const marker = originalAttributeName(name);
    if (element.hasAttribute(marker)) return;

    element.setAttribute(
      marker,
      element.hasAttribute(name) ? element.getAttribute(name) : missingAttribute
    );
  }

  function applyOriginalAttribute(element, name) {
    const marker = originalAttributeName(name);
    if (!element.hasAttribute(marker)) return;

    const value = element.getAttribute(marker);
    if (value === missingAttribute) {
      element.removeAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  }

  // Splide's group/slide semantics are valid on generic wrappers, but not on
  // native links or list items. Preserve author-provided attributes before
  // mount so semantic slide roots can keep their native role and link name.
  function rememberSemanticSlides(el) {
    const list = el.querySelector('.splide__list');
    if (!list) return;

    for (const slide of list.children) {
      if (slide.tagName !== 'A' && slide.tagName !== 'LI') continue;
      for (const name of semanticAttributes) rememberAttribute(slide, name);
    }
  }

  // Controls inside an aria-hidden slide have to leave the tab order. `inert` would
  // do this in one attribute but also kills pointer events, and callers rely on
  // tapping a partly visible card to bring it to the centre.
  function syncHiddenSlideFocus(slide, hidden) {
    const descendants = [...slide.querySelectorAll(focusableSelector)];
    const focusableNodes = slide.matches(focusableSelector)
      ? [slide, ...descendants]
      : descendants;

    for (const node of focusableNodes) {
      if (hidden) {
        rememberAttribute(node, 'tabindex');
        node.setAttribute('tabindex', '-1');
        continue;
      }

      // Restore once, then hand the control back to whichever script owns it while
      // the slide is on screen.
      applyOriginalAttribute(node, 'tabindex');
      node.removeAttribute(originalAttributeName('tabindex'));
    }
  }

  function syncSemanticSlides(el) {
    const slides = el.querySelectorAll('.splide__slide');

    for (const slide of slides) {
      syncHiddenSlideFocus(slide, slide.getAttribute('aria-hidden') === 'true');

      if (slide.tagName !== 'A' && slide.tagName !== 'LI') continue;

      const hasSplideRole = slide.getAttribute('role') === 'group';
      if (hasSplideRole) {
        applyOriginalAttribute(slide, 'role');
        applyOriginalAttribute(slide, 'aria-label');
      }

      if (slide.getAttribute('aria-roledescription') === 'slide') {
        applyOriginalAttribute(slide, 'aria-roledescription');
      }

      if (slide.tagName === 'A') {
        if (slide.getAttribute('aria-hidden') === 'true') {
          slide.setAttribute('tabindex', '-1');
        } else {
          applyOriginalAttribute(slide, 'tabindex');
        }
      }
    }
  }

  function restoreSemanticSlides(el) {
    for (const name of semanticAttributes) {
      const marker = originalAttributeName(name);

      for (const node of el.querySelectorAll('[' + marker + ']')) {
        applyOriginalAttribute(node, name);
        node.removeAttribute(marker);
      }
    }
  }

  function build(el) {
    if (typeof window.Splide === 'undefined') return;

    const layout = el.getAttribute('data-layout') || 'per-page';
    const layoutMobile = el.getAttribute('data-layout-mobile') || layout;
    const gap = num(el, 'data-gap', 0);
    const gapMobile = num(el, 'data-gap-mobile', gap);
    const pagination = el.getAttribute('data-pagination') || 'none';

    promoteSlides(el);
    rememberSemanticSlides(el);

    const list = el.querySelector('.splide__list');
    if (list) {
      for (let i = 0; i < list.children.length; i++) {
        if (list.children[i].classList.contains('carousel__slide')) {
          list.children[i].classList.add('splide__slide');
        }
      }
    }

    // Remote dots follow the pagination setting: 'none' means no dots anywhere,
    // and under 'dots' they stand in for Splide's built-in pagination instead of
    // doubling up with it. 'progress' can still pair with them — mini-doorways
    // shows dots in its header and the bar below the cards.
    const dotsSlot = pagination === 'none' ? null : claimDotsSlot(el);

    // mediaQuery 'min' → the base config is mobile, the 750 breakpoint is desktop.
    const base = sizingFor(el, layoutMobile, gapMobile, true);
    const desktop = sizingFor(el, layout, gap, false);

    const typeAttr = el.getAttribute('data-type');
    const type = typeAttr === 'fade' ? 'fade' : typeAttr === 'loop' ? 'loop' : 'slide';

    const options = {
      type: type,
      role: 'group',
      mediaQuery: 'min',
      speed: num(el, 'data-speed', 400),
      perMove: 1,
      omitEnd: bool(el, 'data-omit-end', true),
      focus: el.getAttribute('data-focus') === 'center' ? 'center' : 0,
      trimSpace: bool(el, 'data-trim-space', true),
      arrows: bool(el, 'data-arrows', false),
      pagination: pagination === 'dots' && !dotsSlot,
      gap: gapMobile + 'px',
      breakpoints: { 750: { gap: gap + 'px' } }
    };

    const easing = el.getAttribute('data-easing');
    if (easing) options.easing = easing;

    // Splide rewrites the arrows' aria-label on every move, so its own English
    // defaults would win over anything the section rendered. Hand it the
    // translations instead; absent attributes leave the defaults in place.
    const prevLabel = el.getAttribute('data-prev-label');
    const nextLabel = el.getAttribute('data-next-label');

    if (prevLabel || nextLabel) {
      options.i18n = {};
      if (prevLabel) options.i18n.prev = prevLabel;
      if (nextLabel) options.i18n.next = nextLabel;
    }

    // Fade has no translate, so free-drag ends in Controller.scroll() at x=0
    // and snaps back to the first slide. Step drag uses go() and stays safe.
    if (options.type === 'fade' || el.getAttribute('data-drag') === 'step') {
      options.drag = true;
    } else {
      options.drag = 'free';
      options.snap = true;
    }

    for (const key in base) options[key] = base[key];
    for (const dkey in desktop) options.breakpoints[750][dkey] = desktop[dkey];

    const pad = paddingOption(el);
    if (pad) {
      options.padding = pad;
      // Same inset above and below 750 — values update on resize via syncPadding.
      options.breakpoints[750].padding = pad;
    }

    const splide = new Splide(el, options);

    if (options.drag === true) limitDragToOneSlide(splide);

    // Runs synchronously, and this handler is registered before any caller binds
    // its own: Splide notifies in registration order, so a component script that
    // manages controls in its own slides still gets the last word.
    splide.on(
      'mounted moved active inactive visible hidden updated resized refresh',
      function () {
        syncSemanticSlides(el);
      }
    );

    if (pagination === 'progress') driveProgressBar(splide, el);
    if (dotsSlot) driveRemoteDots(splide, el, dotsSlot);

    if (pad) {
      splide.on('resize refreshed', function () {
        syncPadding(splide, el);
      });
    }

    // Controls are gated on `.is-overflow` in CSS. Splide maintains the class
    // itself, but callers also read it, so keep it authoritative from mount on.
    let reportedOverflow = false;
    let hadOverflow = false;
    splide.on('overflow', function (isOverflow) {
      reportedOverflow = true;
      el.classList.toggle('is-overflow', !!isOverflow);

      if (isOverflow) {
        hadOverflow = true;
        return;
      }

      // Initial mount can disagree with our overflow test near the threshold —
      // keep the instance and let fitted CSS (e.g. Press) center the row.
      // Only tear down after a real overflowing → fitting transition (resize).
      if (!hadOverflow) return;
      if (bool(el, 'data-force-mount', false)) return;

      queueMicrotask(function () {
        // Hidden panels report a zero-width track; don't tear down on that noise.
        if (!isMeasurable(el)) return;
        if (!fitsTrack(el)) return;
        if (bool(el, 'data-force-mount', false)) return;
        teardown(el);
      });
    });

    splide.mount();
    syncSemanticSlides(el);
    // Splide does not always emit `overflow` for the initial state. Only fill that
    // gap when we can actually page — getEnd() > 0 means there is a next slide.
    // Never add the class when mount() already reported false, or when every slide
    // already fits: that would strand arrows/dots on a track that needs no paging.
    if (!reportedOverflow && splide.Components.Controller.getEnd() > 0) {
      el.classList.add('is-overflow');
    }
    el.__splide = splide;
    el.dispatchEvent(
      new CustomEvent('theme:carousel:mounted', { bubbles: true, detail: { splide: splide } })
    );
  }

  /**
   * A swipe on a step carousel could land two cards on: Splide pushes a quick
   * flick on by up to a full track width, and peeking tracks fit more than one
   * card in that. Remember the slide a drag starts on and let its release move
   * at most one card either way. Splide's drag hands its target straight to
   * Controller.go(), so that is where the step is limited; arrows, dots and
   * keyboard never pass through a drag and are left alone.
   */
  function limitDragToOneSlide(splide) {
    let dragStartIndex = null;

    splide.on('drag', function () {
      dragStartIndex = splide.index;
    });

    // `dragged` fires just before the release calls go(), so clear afterwards
    splide.on('dragged', function () {
      queueMicrotask(function () {
        dragStartIndex = null;
      });
    });

    splide.on('mounted', function () {
      const controller = splide.Components.Controller;
      const go = controller.go;

      controller.go = function (control, allowSameIndex, callback) {
        let target = control;

        if (dragStartIndex !== null && typeof control === 'number') {
          target = dragStartIndex + Math.sign(control - dragStartIndex);
        }

        return go.call(this, target, allowSameIndex, callback);
      };
    });
  }

  function releaseDotsSlot(el) {
    let slot = el.querySelector('[data-carousel-dots-slot]');
    if (!slot) {
      const root = el.closest('.shopify-section');
      slot = root && root.querySelector('[data-carousel-dots-slot]');
    }
    if (!slot || slot.__carouselOwner !== el) return;
    slot.__carouselOwner = null;
    slot.textContent = '';
  }

  function teardown(el) {
    if (!el.__splide) return;

    try {
      el.__splide.destroy(true);
    } catch (error) {
      // Splide may already be mid-destroy from a section unload.
    }

    el.__splide = null;
    el.classList.remove('is-overflow', 'is-initialized');
    restoreSemanticSlides(el);
    releaseDotsSlot(el);
    el.dispatchEvent(new CustomEvent('theme:carousel:destroyed', { bubbles: true }));
  }

  // Do the slides overflow the visible track? The tolerance absorbs sub-pixel
  // rounding on calc()-based slide widths, so an exact N-up row is not mistaken
  // for overflow. A real extra slide adds far more than a few px.
  //
  // clientWidth includes horizontal padding. Dual-bleed carousels
  // (data-padding-inline="section" / CSS padding-inline on the track) reserve
  // that space for inset ends, so subtract it — otherwise slides that overflow
  // the content box can still fail the check and never mount (no arrows/dots).
  function overflows(el) {
    const track = el.querySelector('.splide__track');
    const list = el.querySelector('.splide__list');
    if (!track || !list || !track.clientWidth) return false;

    let available = track.clientWidth;
    const trackStyles = getComputedStyle(track);
    available -= (parseFloat(trackStyles.paddingLeft) || 0)
      + (parseFloat(trackStyles.paddingRight) || 0);
    if (available <= 0) return false;

    return list.scrollWidth - available > num(el, 'data-overflow-tolerance', 1);
  }

  // Splide's verdict (Controller.getEnd(), the `overflow` event) and our own
  // measurement can disagree by a pixel or two near the threshold. Mounting is
  // gated on overflows(), so tearing down on Splide's verdict alone lets the next
  // resize remount a carousel that the resize after that tears down again —
  // flicker, plus controls stranded on a track that cannot page in between.
  // Both tests have to agree the row fits before an instance goes away.
  function fitsTrack(el) {
    if (!el.__splide) return !overflows(el);
    return el.__splide.Components.Controller.getEnd() <= 0 && !overflows(el);
  }

  // Keep Splide track padding in sync when --section-padding-inline changes
  // across breakpoints (Splide options are otherwise fixed at mount).
  function syncPadding(splide, el) {
    const pad = paddingOption(el);
    if (!pad) return;
    const current = splide.options.padding;
    if (
      current &&
      current.left === pad.left &&
      current.right === pad.right
    ) {
      return;
    }
    splide.options = { padding: pad };
  }

  // Rows that are only a carousel on small screens opt out above a width, so
  // growing past it hands the layout back to the section's static CSS.
  function withinMountWidth(el) {
    const max = parseFloat(el.getAttribute('data-mount-max-width'));
    if (isNaN(max)) return true;
    return window.innerWidth <= max;
  }

  // [hidden] / display:none ancestors report 0×0 layout. Refreshing or running
  // fitsTrack there falsely tears down a mounted instance; mounting never works
  // either (overflows() needs a real track width). Callers that reveal a panel
  // (e.g. tab switches) should re-evaluate the now-visible carousel directly.
  function isMeasurable(el) {
    return el.getClientRects().length > 0;
  }

  function evaluate(el) {
    if (!isMeasurable(el)) return;

    if (el.__splide) {
      if (!withinMountWidth(el)) {
        teardown(el);
        return;
      }

      // Splide does not always re-emit `overflow` on viewport resize. When every
      // slide already fits, tear down so static CSS can grow/centre the row and
      // so controls gated on `.is-overflow` disappear.
      try {
        el.__splide.refresh();
      } catch (err) {
        // Splide may already be mid-destroy from a section unload.
      }

      if (el.__splide && fitsTrack(el) && !bool(el, 'data-force-mount', false)) {
        teardown(el);
      }
      return;
    }

    promoteSlides(el);
    bindImageLoads(el);
    if (!withinMountWidth(el)) return;
    if (overflows(el) || bool(el, 'data-force-mount', false)) build(el);
  }

  // Lazy/intrinsic image widths often resolve after the first overflow check.
  // Unmounted: re-run evaluate so a newly overflowing row can mount.
  // Mounted: refresh Splide so slide sizes catch up to the decoded images.
  function bindImageLoads(el) {
    if (el.dataset.carouselImagesBound === 'true') return;
    el.dataset.carouselImagesBound = 'true';

    const images = el.querySelectorAll('img');
    if (!images.length) return;

    let pending = 0;

    function onSettled() {
      pending -= 1;
      if (pending > 0) return;

      if (el.__splide) {
        // Skip refresh while hidden — zero layout can fire a false overflow teardown.
        if (!isMeasurable(el)) return;

        try {
          el.__splide.refresh();
        } catch (err) {
          // Splide may already be mid-destroy from a section unload.
        }
        return;
      }

      evaluate(el);
    }

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (img.complete) continue;
      pending += 1;
      img.addEventListener('load', onSettled, { once: true });
      img.addEventListener('error', onSettled, { once: true });
    }
  }

  function evaluateAll(root) {
    (root || document).querySelectorAll('.splide[data-carousel]').forEach(evaluate);
  }

  function destroyAll(root) {
    (root || document).querySelectorAll('.splide[data-carousel]').forEach(teardown);
  }

  function ready() {
    if (window.Splide) return evaluateAll();
    let tries = 0;
    const timer = setInterval(function () {
      if (window.Splide) {
        clearInterval(timer);
        evaluateAll();
        return;
      }

      if (tries++ > 60) clearInterval(timer);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  // Re-check on resize. Mounted instances self-manage via Splide overflow events
  // (teardown when no longer overflowing); unmounted ones may need to mount.
  let resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { evaluateAll(); }, 200);
  });

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) { evaluateAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    const els = e.target.querySelectorAll('.splide[data-carousel]');
    for (let i = 0; i < els.length; i++) {
      teardown(els[i]);
    }

    const slots = e.target.querySelectorAll('[data-carousel-dots-slot]');
    for (let s = 0; s < slots.length; s++) {
      slots[s].__carouselOwner = null;
      slots[s].textContent = '';
    }
  });

  // Targeted remount/refresh for callers that reveal a carousel (tab panels,
  // accordions) without faking a window resize — which would evaluateAll and
  // risk tearing down hidden sibling instances.
  window.themeCarousel = {
    destroyAll: destroyAll,
    evaluate: evaluate,
    evaluateAll: evaluateAll
  };
})();
